<div class="forecast-shell">
  <section class="hero-card">
    <div class="hero-copy">
      <div class="eyebrow">Auto fetch</div>
      <h1>Forecast Market Price</h1>
      <p>Fetch DAM, GDAM, and RTM forecast market prices, store them in the new snapshot table, and inspect saved blockwise rows for any date.</p>
    </div>
    <div class="hero-icon">
      <lucide-icon [img]="CalendarRangeIcon" [size]="28"></lucide-icon>
    </div>
  </section>

  <section class="action-card">
    <div class="form-grid">
      <label>
        <span>Market</span>
        <select [ngModel]="form().product" (ngModelChange)="updateField('product', $event)">
          @for (product of productOptions; track product) {
            <option [value]="product">{{ product }}</option>
          }
        </select>
      </label>
      <label>
        <span>Forecast date</span>
        <input type="date" [ngModel]="form().forecast_date" (ngModelChange)="updateField('forecast_date', $event)" />
      </label>
      <button type="button" class="primary-btn" [disabled]="fetching()" (click)="fetchSnapshot()">
        <lucide-icon [img]="DownloadIcon" [size]="16"></lucide-icon>
        <span>{{ fetching() ? 'Fetching...' : 'Fetch Selected Market' }}</span>
      </button>
      <button type="button" class="secondary-btn" [disabled]="fetchingAll()" (click)="fetchAllProducts()">
        <lucide-icon [img]="DatabaseIcon" [size]="16"></lucide-icon>
        <span>{{ fetchingAll() ? 'Syncing...' : 'Fetch DAM + GDAM + RTM' }}</span>
      </button>
    </div>
    <div class="hint">Stored snapshots are filtered by market and date. Select a saved snapshot to view the 96 quarter-hour price blocks.</div>
  </section>

  <div class="content-grid">
    <section class="list-card">
      <div class="section-head">
        <h2>Stored Snapshots</h2>
        <span>{{ snapshots().length }} {{ form().product }} snapshots</span>
      </div>

      @if (loading()) {
        <div class="empty-state">Loading forecast market prices...</div>
      } @else if (snapshots().length === 0) {
        <div class="empty-state">No stored {{ form().product }} forecast market prices for {{ formatDisplayDate(form().forecast_date) }}.</div>
      } @else {
        <div class="report-list">
          @for (snapshot of snapshots(); track snapshot.id) {
            <div class="report-item" [class.active]="activeSnapshotId() === snapshot.id" (click)="selectSnapshot(snapshot.id)">
              <div class="report-copy">
                <strong>{{ snapshot.product }} • {{ formatDisplayDate(snapshot.forecast_date) }}</strong>
                <span>{{ snapshot.row_count }} blocks • {{ snapshot.request_method }}</span>
              </div>
              <div class="report-actions" (click)="$event.stopPropagation()">
                <button type="button" class="icon-btn" title="Open source" (click)="openSource(snapshot)">
                  <lucide-icon [img]="DatabaseIcon" [size]="15"></lucide-icon>
                </button>
                <button type="button" class="icon-btn" title="Refresh snapshot" [disabled]="isRefetching(snapshot.id)" (click)="refetchSnapshot(snapshot)">
                  <lucide-icon [img]="RefreshCcwIcon" [size]="15"></lucide-icon>
                </button>
              </div>
            </div>
          }
        </div>
      }
    </section>

    <section class="detail-card">
      <div class="section-head">
        <h2>Blockwise Data</h2>
        @if (selectedSnapshot()) {
          <span>{{ selectedSnapshot()?.product }} • {{ formatDisplayDate(selectedSnapshot()?.forecast_date) }}</span>
        }
      </div>

      @if (!selectedSnapshot()) {
        <div class="empty-state">Select a stored snapshot to view blockwise market price data.</div>
      } @else {
        <div class="summary-strip">
          <div>
            <small>Market</small>
            <strong>{{ selectedSnapshot()?.product }}</strong>
          </div>
          <div>
            <small>Date</small>
            <strong>{{ formatDisplayDate(selectedSnapshot()?.forecast_date) }}</strong>
          </div>
          <div>
            <small>Rows stored</small>
            <strong>{{ selectedSnapshot()?.row_count }}</strong>
          </div>
          <div>
            <small>Last fetched</small>
            <strong>{{ formatDisplayDate(selectedSnapshot()?.last_refetched_at || selectedSnapshot()?.created_at) }}</strong>
          </div>
        </div>

        <section class="chart-card">
          <div class="chart-head">
            <div>
              <h3>Forecast Market Price Graph</h3>
              <p>Selected snapshot across all 96 quarter-hour blocks.</p>
            </div>
            <div class="chart-legend">
              <span *ngFor="let line of chartLines()">
                <i [style.background]="line.color"></i>
                {{ line.label }}
                <b>{{ line.latestValue !== null ? formatPrice(line.latestValue) : '-' }}</b>
              </span>
            </div>
          </div>

          <div class="chart-plot" *ngIf="chartLines().length; else noChart">
            <svg viewBox="0 0 960 220" preserveAspectRatio="none" aria-label="Forecast market price graph" (mouseleave)="clearBlockTooltip()">
              <path d="M24 20 L24 190 L936 190" class="chart-axis"></path>
              <path d="M24 20 L936 20" class="chart-grid major"></path>
              <path d="M24 76.67 L936 76.67" class="chart-grid"></path>
              <path d="M24 133.33 L936 133.33" class="chart-grid"></path>
              <path d="M24 190 L936 190" class="chart-grid"></path>

              <ng-container *ngFor="let hourBlock of forecastHourBlocks">
                <path [attr.d]="'M' + chartX(hourBlock) + ' 20 L' + chartX(hourBlock) + ' 190'" class="chart-grid vertical"></path>
              </ng-container>

              <path [attr.d]="'M' + currentBlockX() + ' 20 L' + currentBlockX() + ' 190'" class="chart-current-line"></path>
              <path *ngIf="hoveredBlockX() !== null" [attr.d]="'M' + hoveredBlockX() + ' 20 L' + hoveredBlockX() + ' 190'" class="chart-hover-line"></path>

              <ng-container *ngFor="let line of chartLines()">
                <path *ngIf="line.areaPath" [attr.d]="line.areaPath" [attr.fill]="line.fill"></path>
                <polyline [attr.points]="line.points" [attr.stroke]="line.color" class="chart-series-line"></polyline>
                <circle *ngIf="line.currentPoint" [attr.cx]="line.currentPoint.split(',')[0]" [attr.cy]="line.currentPoint.split(',')[1]" r="4.5" [attr.fill]="line.color" class="chart-current-dot"></circle>
              </ng-container>

              <g class="chart-hover-zones">
                <rect
                  *ngFor="let block of chartBlocks()"
                  [attr.x]="block.leftX"
                  y="20"
                  [attr.width]="block.width"
                  height="170"
                  class="chart-hover-zone"
                  (mouseenter)="showBlockTooltip(block.blockNumber)"
                  (mousemove)="showBlockTooltip(block.blockNumber)"
                  (focus)="showBlockTooltip(block.blockNumber)"
                ></rect>
              </g>
            </svg>

            <div class="chart-tooltip" *ngIf="hoveredChartBlock() as tooltip" [style.left.%]="hoveredTooltipLeft()">
              <small>Block {{ tooltip.blockNumber }} • {{ tooltip.blockLabel }}</small>
              <strong>Market: {{ formatPrice(tooltip.marketPrice) }}</strong>
              <strong>Source: {{ formatPrice(tooltip.sourcePrice) }}</strong>
              <strong>MCP: {{ formatPrice(tooltip.mcpPrice) }}</strong>
            </div>

            <div class="chart-y-scale">
              <span>{{ formatPrice(chartBounds().max) }}</span>
              <span>{{ formatPrice((chartBounds().max + chartBounds().min) / 2) }}</span>
              <span>{{ formatPrice(chartBounds().min) }}</span>
            </div>
          </div>

          <div class="chart-x-scale">
            <span *ngFor="let hourBlock of forecastHourBlocks">{{ hourLabel(hourBlock) }}</span>
          </div>

          <div class="chart-metrics">
            <div class="chart-metric" *ngFor="let line of chartLines()">
              <small>{{ line.label }} Range</small>
              <strong>{{ line.minValue !== null ? formatPrice(line.minValue) : '-' }} to {{ line.maxValue !== null ? formatPrice(line.maxValue) : '-' }}</strong>
            </div>
            <div class="chart-metric">
              <small>Current Block</small>
              <strong>{{ currentBlockLabel() }}</strong>
            </div>
          </div>
        </section>

        <ng-template #noChart>
          <div class="empty-state">No graph data available for the selected snapshot.</div>
        </ng-template>

        <div class="table-wrap">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Block</th>
                  <th>Time Range</th>
                  <th>Market Price(Rs)</th>
                  <th>Source Price</th>
                  <th>MCP Price</th>
                </tr>
              </thead>
              <tbody>
                @for (block of selectedSnapshot()?.blocks || []; track block.block_number) {
                  <tr>
                    <td>{{ block.block_number }}</td>
                    <td>{{ block.block_label }}</td>
                    <td>{{ formatPrice(block.market_price) }}</td>
                    <td>{{ formatPrice(block.source_price) }}</td>
                    <td>{{ formatPrice(block.mcp_price) }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        <div class="block-grid">
          @for (group of groupedBlocks(); track group.hourLabel) {
            <section class="hour-card">
              <header>{{ group.hourLabel }}</header>
              <div class="hour-rows">
                @for (block of group.items; track block.block_number) {
                  <div class="block-chip">
                    <span>B{{ block.block_number }}</span>
                    <strong>{{ formatPrice(block.market_price) }}</strong>
                  </div>
                }
              </div>
            </section>
          }
        </div>
      }
    </section>
  </div>
</div>
